import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Phone, Mail } from "lucide-react";
import { API } from "../lib/api";
import Logo from "../components/Logo";

const EASE = [0.22, 1, 0.36, 1];

export default function ImpiantiPage() {
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null); // index or null

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((r) => setData(r.data))
      .catch(() => setData({ title: "Impianti realizzati", subtitle: "", items: [] }));
  }, []);

  const items = data?.items || [];

  const step = useCallback(
    (d) => setLightbox((i) => (i === null ? null : (i + d + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, step]);

  return (
    <div className="min-h-screen bg-white" data-testid="impianti-page">
      {/* Header */}
      <header className="sticky top-0 z-40 h-16 px-4 sm:px-6 flex items-center justify-between border-b border-line bg-white/80 backdrop-blur-md">
        <Logo />
        <Link
          to="/"
          data-testid="back-to-deck-link"
          className="inline-flex items-center gap-2 text-sm font-display font-semibold text-slate-500 hover:text-ink transition-colors"
        >
          <ArrowLeft size={16} />
          Torna alla presentazione
        </Link>
      </header>

      {/* Hero */}
      <section className="px-6 sm:px-12 lg:px-20 xl:px-28 pt-16 sm:pt-20 pb-10">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-2.5 h-2.5 bg-brand" />
              <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-slate-500">
                Sul campo
              </span>
            </div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-ink max-w-3xl">
              {data?.title || "Impianti realizzati"}
            </h1>
            {data?.subtitle && (
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">{data.subtitle}</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Photo grid */}
      <section className="px-6 sm:px-12 lg:px-20 xl:px-28 pb-24">
        <div className="max-w-7xl mx-auto">
          {!data ? (
            <div className="font-mono text-xs text-slate-400 uppercase tracking-widest py-20 text-center">
              Caricamento…
            </div>
          ) : items.length === 0 ? (
            <p className="text-slate-500 py-20 text-center">Nessuna foto disponibile al momento.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((it, i) => (
                <motion.button
                  key={i}
                  data-testid={`photo-card-${i}`}
                  onClick={() => setLightbox(i)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: EASE }}
                  className="group text-left bg-white border border-line rounded-xl overflow-hidden transition-shadow duration-300 hover:shadow-xl hover:shadow-slate-200"
                >
                  <div className="h-64 overflow-hidden">
                    <img
                      src={it.image}
                      alt={it.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">
                      IMP.{String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-1 font-display font-semibold text-ink leading-snug">{it.title}</div>
                    {it.desc && <div className="mt-0.5 text-sm text-slate-500">{it.desc}</div>}
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-graphite text-white px-6 sm:px-12 lg:px-20 xl:px-28 py-12">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="font-display font-extrabold tracking-tight text-xl">Proaura srls</div>
            <div className="mt-1 text-sm text-white/50">Via dei Casali Molinario, 3 - 00189 Roma</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-8">
            <a href="tel:800933630" className="inline-flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors">
              <Phone size={15} /> 800 933 630
            </a>
            <a href="mailto:proaurasrl@gmail.com" className="inline-flex items-center gap-2.5 text-sm text-white/70 hover:text-white transition-colors">
              <Mail size={15} /> proaurasrl@gmail.com
            </a>
          </div>
        </div>
      </footer>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 bg-[#090A0C]/95 backdrop-blur-sm flex flex-col"
            onClick={() => setLightbox(null)}
          >
            <div className="flex items-center justify-between px-5 h-16 shrink-0">
              <span className="font-mono text-xs text-white/50 uppercase tracking-[0.25em]">
                IMP.{String(lightbox + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </span>
              <button
                data-testid="lightbox-close"
                onClick={() => setLightbox(null)}
                className="p-2.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center px-4 sm:px-16 min-h-0" onClick={(e) => e.stopPropagation()}>
              <button
                data-testid="lightbox-prev"
                onClick={() => step(-1)}
                className="p-3 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0 mr-2 sm:mr-6"
              >
                <ChevronLeft size={24} />
              </button>
              <motion.figure
                key={lightbox}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="max-w-4xl w-full"
              >
                <img
                  src={items[lightbox].image}
                  alt={items[lightbox].title}
                  className="w-full max-h-[70vh] object-contain rounded-lg"
                />
                <figcaption className="mt-4 text-center">
                  <div className="font-display font-semibold text-white">{items[lightbox].title}</div>
                  {items[lightbox].desc && (
                    <div className="mt-1 text-sm text-white/50">{items[lightbox].desc}</div>
                  )}
                </figcaption>
              </motion.figure>
              <button
                data-testid="lightbox-next"
                onClick={() => step(1)}
                className="p-3 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0 ml-2 sm:ml-6"
              >
                <ChevronRight size={24} />
              </button>
            </div>
            <div className="h-10 shrink-0" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
